import { ImageFragment } from "../../fragments/image";
import { LearnFragment } from "../../fragments/learn";
import { gql } from "@apollo/client";

export const QUERY_COURSE = gql`
  query course($slug: [String]) {
    courses: courses_connection(
      pagination: { limit: 1 }
      filters: { slug: { in: $slug }, publishedAt: { notNull: true } }
      status: PUBLISHED
    ) {
      data {
        id
        documentId
        attributes {
          title
          slug
          description
          duration
          textContent {
            markdownText
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          courseCompletedSection {
            title
            message
          }
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          hero {
            data {
              ...ImageFragment
            }
          }
          modules {
            id
            title
            description
            moduleStableId
            lessons {
              id
              title
              description
              lessonStableId
              duration
              textContent {
                markdownText
              }
              thumbnail {
                data {
                  ...ImageFragment
                }
              }
              authors: authors_connection {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const QUERY_COURSE_LESSON = gql`
  query courseLesson($slug: [String]) {
    courses: courses_connection(
      pagination: { limit: 1 }
      filters: { slug: { in: $slug }, publishedAt: { notNull: true } }
      status: PUBLISHED
    ) {
      data {
        id
        documentId
        attributes {
          title
          slug
          duration
          description
          feedbackQuestions {
            question
          }
          textContent {
            markdownText
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          hero {
            data {
              ...ImageFragment
            }
          }
          modules {
            id
            title
            description
            moduleStableId
            lessons {
              id
              duration
              title
              description
              lessonStableId
              textContent {
                markdownText
              }
              objectivesSection {
                id
                title
                objectives {
                  description
                }
              }
              thumbnail {
                data {
                  ...ImageFragment
                }
              }
              highlightContent {
                audio {
                  audio {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  transcription
                }
                embedded {
                  link
                  provider
                }
                image {
                  media {
                    data {
                      attributes {
                        url
                        alternativeText
                        mime
                      }
                    }
                  }
                }
              }
              downloadableContent {
                title
                description
                content {
                  data {
                    attributes {
                      url
                    }
                  }
                }
              }
              faq {
                title
                description
              }
              authors: authors_connection {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const QUERY_FETCH_LATEST_COURSES = gql`
  query fetchLatestCourses($slug: [String]) {
    courses: courses_connection(
      pagination: { limit: 2 }
      filters: { slug: { notIn: $slug }, publishedAt: { notNull: true } }
      status: PUBLISHED
    ) {
      data {
        id
        documentId
        attributes {
          title
          slug
          description
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          modules {
            id
            lessons {
              id
              authors {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const FETCH_COURSES = gql`
  query fetchCourses($limit: Int, $publishedAt: [String] = "publishedAt:asc") {
    courses: courses_connection(
      pagination: { limit: $limit }
      status: PUBLISHED
      sort: $publishedAt
      filters: { publishedAt: { notNull: true } }
    ) {
      data {
        id
        documentId
        attributes {
          title
          slug
          duration
          description
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          modules {
            id
            lessons {
              id
              authors: authors_connection {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${ImageFragment}
`;

export const FETCH_COURSES_PAGEABLE = gql`
  query fetchCoursesPageable($page: Int!, $search: String) {
    courses: courses_connection(
      pagination: { pageSize: 8, page: $page }
      filters: { title: { containsi: $search }, publishedAt: { notNull: true } }
      status: PUBLISHED
    ) {
      data {
        id
        attributes {
          title
          slug
          description
          duration
          thumbnail {
            data {
              ...ImageFragment
            }
          }
          course_category {
            data {
              attributes {
                name
                description
              }
            }
          }
          modules {
            id
            lessons {
              id
              authors: authors_connection {
                data {
                  attributes {
                    name
                    role
                    bio
                    avatar {
                      data {
                        ...ImageFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      meta {
        pagination {
          page
          pageCount
          total
        }
      }
    }
  }
  ${ImageFragment}
`;

export const FETCH_LEARN = gql`
  query fetchLearn($staffId: ID!, $completed: Boolean) {
    learn: learns_connection(
      filters: {
        users_permissions_user: { documentId: { eq: $staffId } }
        completed: { eq: $completed }
      }
    ) {
      data {
        id
        documentId
        attributes {
          ...LearnFragment
        }
      }
    }
  }
  ${LearnFragment}
`;

export const FETCH_COURSE_LEARN = gql`
  query fetchCourseLearn($staffId: ID!, $courseSlug: String!) {
    learn: learns_connection(
      filters: {
        users_permissions_user: { documentId: { eq: $staffId } }
        course: { slug: { eq: $courseSlug } }
      }
    ) {
      data {
        id
        documentId
        attributes {
          ...LearnFragment
        }
      }
    }
  }
  ${LearnFragment}
`;

export const QUERY_COURSE_PAGE = gql`
  query CoursePage {
    coursePage {
      data {
        attributes {
          lesson {
            beginButtonLabel
            watchAgainButtonlabel
            lockedButtonlabel
            offlineModeLockedButtonLabel
          }
        }
      }
    }
  }
`;
